import React, { FC, Fragment, useEffect, useState, useContext } from 'react';
import axios, { CancelTokenSource } from 'axios';
import {
  Theme,
  Grid,
  Container,
  Typography,
  Button,
  Tooltip,
  IconButton,
  CircularProgress,
  makeStyles,
  Menu,
  MenuItem,
  Fade,
  Badge,
  Collapse,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@material-ui/core';
import {
  INVOICE_PAYMENT_BASE_URL,
  GET_INVOICE_PAYMENT_RETURN_BASE_URL,
  RETURN_INVOICE_BASE_URL,
  EXPORT_INVOICE_PAYMENT_BASE_URL,
  EXPORT_INVOICE_PAYMENT_SALES_BASE_URL,
  ZONE_BASE_URL,
  GET_INVOICE_PAYMENT_DETAIL_BASE_URL
} from 'constants/url';
import { Page, PaperCustom, Breadcrumb, StandardConfirmationDialog } from 'components';
import useRouter from 'hooks/useRouter';
import useRole from 'hooks/useRole';
import { format, startOfMonth } from 'date-fns';
import ArrowIcon from '@material-ui/icons/ArrowDropDown';
import CalendarIcon from '@material-ui/icons/EventNote';
import RefreshIcon from '@material-ui/icons/Refresh';
import Pagination from '@material-ui/lab/Pagination';
import _ from 'lodash';
import { CurrentUserContext } from 'contexts/CurrentUserContext';
import { GREEN, WHITE, BLUE_PRIMARY } from 'constants/colors';
import { dummyZone } from 'utils/dummy';
import DateRangeFilter from 'components/DateRangeFilter';
import PaymentTable from './components/PaymentTable';
import PaymentGiroModal from './components/PaymentGiroModal';
import { AddBox, Description, ExitToApp, Search, GetApp } from '@material-ui/icons';
import { months } from 'utils';
import TypeUser from 'typings/enum/TypeUser';
import PaginationCustom from 'components/PaginationCustom';
import InvoicePaymentAuthorization from './components/InvoicePaymentAuthorization';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
const useStyles = makeStyles((theme: Theme) => ({
  refresh: {
    backgroundColor: GREEN,
    color: WHITE,
    '&:hover': {
      backgroundColor: GREEN
    }
  },
  calendarIcon: {
    fontSize: 30,
    color: theme.palette.primary.main
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12
  },
  actionButton: {
    color: WHITE,
    background: BLUE_PRIMARY,
    width: 100,
    align: 'center'
  },
  exportButton: {
    color: WHITE,
    background: BLUE_PRIMARY,
    align: 'center',
    marginLeft: theme.spacing(1)
  },
  menuList: {
    minHeight: 30
  },
  buttonBack: {
    transform: `rotate(-180deg)`
  }
}));

const PaymentPage: FC = () => {
  const { history } = useRouter();
  const classes = useStyles();

  const { currentUser } = useContext(CurrentUserContext);
  const [count, setCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [invoicePayments, setInvoicePayments] = useState<InvoicePaymentModel[]>([]);
  const [invoicePaymentReturn, setInvoicePaymentReturn] = useState<InvoiceReturnModel[]>([]);

  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('id');
  const [name, setName] = useState<string>('');
  const [salesName, setSalesName] = useState<string>('');
  const [giroNumber, setGiroNumber] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [totalPay, setTotalPay] = useState<string>('');
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [payDate, setPayDate] = useState<Date>(new Date());
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [zones, setZones] = useState<ZoneModel[]>([dummyZone]);
  const [selectedZone, setSelectedZone] = useState<number[]>([]);
  const [giroMonthDue, setGiroMonthDue] = useState<{ total: Number; month: string }[]>([]);

  const [total, setTotal] = useState<number>(0);
  const [to, setTo] = useState<number>(0);
  const [from, setFrom] = useState<number>(0);

  const [openCollapse, setOpenCollapse] = useState<boolean>(false);
  const [indexCollapse, setIndexCollapse] = useState<number>(0);
  const [isLoadingCollapse, setLoadingCollapse] = useState<boolean>(false);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [openGiroModal, setOpenGiroModal] = useState<boolean>(false);
  const [isLoadingButton, setIsLoadingButton] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [startDate, setStartDate] = useState<string>(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [openCalendarFilter, setOpenCalendarFilter] = useState<boolean>(false);
  const [loadingExport, setLoadingExport] = useState<boolean>(false);
  const [loadingExportSales, setLoadingExportSales] = useState<boolean>(false);
  const [openExportSales, setOpenExportSales] = useState<boolean>(false);
  const [exportSalesMonth, setExportSalesMonth] = useState<Date>(new Date());
  const [globalSearch, setGlobalSearch] = useState('');

  const [snackbarVariant, setSnackbarVariant] = useState<'success' | 'error'>('success');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const isSuperAdmin = useRole({
    type: (currentUser && currentUser.type) || TypeUser.SALES,
    allowed: [TypeUser.SUPERADMIN]
  });
  const isHeadAdminOps = useRole({
    type: (currentUser && currentUser.type) || TypeUser.SALES,
    allowed: [TypeUser.HEAD_ADMIN_OPS]
  });
  const [totalAuthorization, setTotalAuthorization] = useState<number>(0);
  const isSuperVisor = useRole({
    type: (currentUser && currentUser.type) || TypeUser.SALES,
    allowed: [TypeUser.SUPERVISOR]
  });

  const isSales = useRole({
    type: (currentUser && currentUser.type) || TypeUser.SALES,
    allowed: [TypeUser.SALES]
  });

  const isFinance = useRole({
    type: (currentUser && currentUser.type) || TypeUser.ADMIN04,
    allowed: [TypeUser.ADMIN04]
  });

  const salesRoute = currentUser && currentUser.SalesRoute ? currentUser.SalesRoute.map(value => value.ZoneId) : [];
  let cancelToken: CancelTokenSource = axios.CancelToken.source();
  const [authorizaion, setAuthorizaion] = useState<boolean>(false);
  const [reject, setReject] = useState<boolean>(false);
  const [level, setLevel] = useState(0);
  const [isAction, setIsAction] = useState<{
    isOpen: boolean;
    isLoading: boolean;
    id: number;
  }>({
    isOpen: false,
    isLoading: false,
    id: 0
  });
  const [selectedInvoicePayment, setSelectedInvoicePayment] = useState<InvoicePaymentModel | undefined>(undefined);

  const getQueryParams = () => {
    const params = new URLSearchParams();
    params.append('summary', 'list');

    if (name) {
      params.append('partnerName', name);
    }

    if (code) {
      params.append('paymentNumber', code);
    }

    if (invoiceNumber) {
      params.append('invoiceNumber', invoiceNumber);
    }

    if (status) {
      params.append('status', status);
    }

    if (salesName) {
      params.append('sales', salesName);
    }

    if (giroNumber) {
      params.append('giroNumber', giroNumber);
    }
    if (authorizaion) {
      params.append('auhtorization', 'true');
    } else if (startDate && endDate) {
      params.append('startDate', startDate);
      params.append('endDate', endDate);
    }

    if (paymentMethod) {
      params.append('method', paymentMethod);
    }

    if (!!level && authorizaion) {
      params.append('level', String(level));
    }

    if (reject) {
      params.append('reject', String(true));
    }

    if (selectedZone.length > 0) {
      params.append('Zone', selectedZone.join(','));
    } else if (currentUser && (isSuperVisor || isHeadAdminOps)) {
      params.append('Zone', (currentUser.SalesRoute && currentUser.SalesRoute.map(value => value.ZoneId).join(',')) || '');
    }

    if (currentUser && isSales) {
      params.append('SalesId', currentUser.id.toString());
    }

    if (orderBy || order) {
      params.append('orderBy', orderBy);
      params.append('ordered', order);
    }

    if (globalSearch) {
      params.append('globalSearch', globalSearch);
      params.delete('startDate');
      params.delete('endDate');
    }

    params.append('page', currentPage.toString());
    return params.toString();
  };

  const fetchData = async () => {
    setIsLoadingData(true);

    try {
      const { data } = await axios.get(`${INVOICE_PAYMENT_BASE_URL}?${getQueryParams()}`, { cancelToken: cancelToken.token });
      setInvoicePayments(data.data);
      setCount(data.meta.last_page);
      setTotal(data.meta.total);
      setTo(data.meta.to);
      setFrom(data.meta.from);
    } catch (error) {
      console.log('error', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const loadAll = async () => {
    const params = new URLSearchParams();
    params.append('route', salesRoute.join(','));
    params.append('orderBy', 'name');
    params.append('ordered', 'asc');
    params.append('perPage', '1000');

    try {
      const result = await Promise.all([axios.get(`${ZONE_BASE_URL}?${params.toString()}`), axios.get(`${INVOICE_PAYMENT_BASE_URL}/giro-month-due`)]);
      const [zn, gr] = result;
      setZones(zn.data.data);
      setGiroMonthDue(
        (gr.data.data.length > 0 &&
          gr.data.data.map((value: any) => ({
            total: value.total,
            month: months[value.month_number - 1]
          }))) ||
          []
      );
    } catch (error) {
      console.log('error :', error);
    } finally {
    }
  };

  const fetchReturn = async (id: number) => {
    const returnId = invoicePayments.find(f => f.id === id);

    if (!returnId?.ReturnIdMultiple?.length) return;
    setLoadingCollapse(true);

    try {
      const invoiceReturn = await axios.get(`${RETURN_INVOICE_BASE_URL}?id=${returnId.ReturnIdMultiple.map((v: any) => v.ReturnId).join(',')}`);
      setInvoicePaymentReturn(invoiceReturn.data.data);
    } catch (e) {
      console.log('error', e);
    } finally {
      setLoadingCollapse(false);
    }
  };

  const onRefresh = () => {
    setCount(0);
    setCurrentPage(1);
    setInvoicePayments([]);
    setInvoicePaymentReturn([]);
    setOrder('desc');
    setName('');
    setCode('');
    setTotal(0);
    setTo(0);
    setFrom(0);
    setOpenCollapse(false);
    setLoadingCollapse(false);

    fetchData();
  };

  const handleCreatePayment = () => {
    history.push('/pembayaran-penjualan/tambah');
  };

  const handleOpenCollapse = (index: number): React.MouseEventHandler => () => {
    setIndexCollapse(index);
    setOpenCollapse(prevState => (prevState ? (index === indexCollapse ? false : true) : true));
    setInvoicePaymentReturn([]);
    fetchReturn(index);
  };

  const handleCalendarFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenCalendarFilter(!openCalendarFilter);
  };

  const handleUpdatePaymentIndividual = (id: number, data: InvoicePaymentModel) => {
    const currentInvoicePayments = [...invoicePayments];

    const findIndex = currentInvoicePayments.findIndex(invoice => invoice.id === id);
    currentInvoicePayments[findIndex] = data;
    setInvoicePayments(currentInvoicePayments);
  };

  const handleCloseSnackbar = (): void => {
    setOpenSnackbar(false);
  };

  const handleSnackBar = (open: boolean, variant: 'success' | 'error', message: string) => {
    setSnackbarVariant(variant);
    setOpenSnackbar(open);
  };

  const handleOpenGiroModal = () => {
    setOpenGiroModal(true);
  };

  const handleCloseGiroModal = () => {
    setOpenGiroModal(false);
  };

  const handleAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadExcel = async () => {
    setLoadingExport(true);

    try {
      const { data } = await axios.get(`${EXPORT_INVOICE_PAYMENT_BASE_URL}?${getQueryParams()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `Pembayaran-${format(new Date(), 'dd/MM/yyyy-HH:mm:ss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.log('error :', error);
    } finally {
      setLoadingExport(false);
    }
  };

  const downloadSalesExcel = async () => {
    setLoadingExportSales(true);

    try {
      const month = format(exportSalesMonth, 'MM');
      const year = format(exportSalesMonth, 'yyyy');
      const { data } = await axios.get(`${EXPORT_INVOICE_PAYMENT_SALES_BASE_URL}?month=${month}&year=${year}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `Pembayaran-Penjualan-${format(new Date(), 'dd/MM/yyyy-HH:mm:ss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      setOpenExportSales(false);
    } catch (error) {
      console.log('error :', error);
    } finally {
      setLoadingExportSales(false);
    }
  };

  useEffect(() => {
    const debouncedFetchHits = _.debounce(fetchData, 500);
    debouncedFetchHits();
    return () => cancelToken.cancel('No longer latest query');
  }, [
    orderBy,
    order,
    currentPage,
    name,
    code,
    selectedZone,
    invoiceNumber,
    status,
    paymentMethod,
    startDate,
    endDate,
    salesName,
    giroNumber,
    isSuperVisor,
    isSales,
    authorizaion,
    level,
    isFinance,
    isSuperAdmin,
    reject,
    globalSearch
  ]);

  useEffect(() => {
    if (!name && !code && !selectedZone && !invoiceNumber && !status && !startDate && !endDate && !paymentMethod && !salesName && !giroNumber) {
      return;
    }

    setCurrentPage(1);
  }, [name, code, selectedZone, invoiceNumber, status, paymentMethod, startDate, endDate, salesName, giroNumber]);

  useEffect(() => {
    loadAll();
    getNotif();
  }, []);

  useEffect(() => {
    if (!isHeadAdminOps) {
      return;
    }

    const assignedZones = salesRoute.map(value => Number(value));
    setSelectedZone(prev => prev.filter(zoneId => assignedZones.includes(zoneId)));
  }, [isHeadAdminOps, currentUser]);

  const isAddAllowed = useRole({
    type: (currentUser && currentUser.type) || TypeUser.SALES,
    allowed: [TypeUser.ADMIN, TypeUser.SUPERADMIN, TypeUser.SALES, TypeUser.ADMIN04, TypeUser.HEAD_ADMIN_OPS]
  });
  const canCreatePayment = isAddAllowed || isHeadAdminOps;

  const handleOpenAuthorization = () => {
    setAuthorizaion(true);
    setLevel(isFinance ? 1 : isSuperAdmin ? 2 : 0);
  };
  const handleCloseAuthorization = () => {
    setAuthorizaion(false);
    setLevel(0);
  };

  const getNotif = async () => {
    try {
      const { data } = await axios.get(`${INVOICE_PAYMENT_BASE_URL}/autorization`);
      setTotalAuthorization(data);
    } catch (error) {
      console.log(error);
    }
  };
  const handleActionAuthorization = (isOpen: boolean, id: number) => {
    if (!isOpen) {
      setIsAction(prev => ({ ...prev, isOpen: false, id: 0 }));
      setSelectedInvoicePayment(undefined);
      return;
    }

    setIsAction(prev => ({ ...prev, isOpen: true, isLoading: true, id }));
    setSelectedInvoicePayment(undefined);

    void (async () => {
      try {
        const { data } = await axios.get(GET_INVOICE_PAYMENT_DETAIL_BASE_URL(id));
        setSelectedInvoicePayment(data.data);
        setIsAction(prev => ({ ...prev, isLoading: false }));
      } catch (error) {
        console.log(error);
        handleSnackBar(true, 'error', 'Gagal memuat detail pembayaran.');
        setIsAction(prev => ({ ...prev, isOpen: false, isLoading: false, id: 0 }));
      }
    })();
  };

  const auhtorization = async () => {
    setIsAction(prev => ({ ...prev, isLoading: true }));
    try {
      const { data } = await axios.get(`${INVOICE_PAYMENT_BASE_URL}/autorization/${isAction.id}`);
      setIsAction(prev => ({ ...prev, isLoading: false, isOpen: false }));
      fetchData();
      handleSnackBar(true, 'success', 'Autorisasi berhasil.');
    } catch (error) {
      console.log(error);
      handleSnackBar(true, 'error', 'Autorisasi gagal.');
    }
  };

  const rejected = async () => {
    setIsAction(prev => ({ ...prev, isLoading: true }));
    try {
      const { data } = await axios.get(`${INVOICE_PAYMENT_BASE_URL}/reject/${isAction.id}`);
      setIsAction(prev => ({ ...prev, isLoading: false, isOpen: false }));
      fetchData();
      getNotif();
      //handleSnackBar(true, 'success', 'Autorisasi berhasil.');
    } catch (error) {
      console.log(error);
      //handleSnackBar(true, 'error', 'Autorisasi gagal.');
    }
  };

  const handleLevel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setLevel(p => (p === +e.currentTarget.value ? 0 : +e.currentTarget.value));
    setReject(p => (p ? false : p));
  };

  const handleReject = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    setReject(p => !p);
    setLevel(p => (p > 0 ? 0 : p));
  };

  return (
    <Page title='Pembayaran Penjualan'>
      <Container>
        <Grid container direction='row' spacing={1}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography variant='h1'> Pembayaran Penjualan</Typography>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Breadcrumb />
          </Grid>
        </Grid>

        <PaperCustom>
          <Grid container direction='row' alignItems='center' item lg={12} md={12} sm={12} xs={12} spacing={2}>
            <Grid item xl={1} lg={1} md={1} sm={6} xs={6}>
              <Tooltip title='Memuat Ulang'>
                <Button onClick={onRefresh} color='inherit' className={classes.refresh}>
                  <RefreshIcon />
                </Button>
              </Tooltip>
            </Grid>

            <Grid item xl={5} lg={5} md={5} sm={6} xs={6}>
              <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                <Typography>{`Menampilkan ${total || 0} pembayaran (${from || 0} - ${to || 0} dari ${total || 0})`}</Typography>
              </Grid>
              <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                {startDate && endDate && (
                  <Typography variant='body2' style={{ fontSize: 12, color: BLUE_PRIMARY }}>
                    Data tanggal {format(new Date(startDate), 'dd-MM-yyyy')} s/d {format(new Date(endDate), 'dd-MM-yyyy')}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Grid item container justify='space-between' alignItems='center' spacing={2} xl={6} lg={6} md={6} sm={12} xs={12}>
              <Grid item sm={12} xs={12} container justify='flex-end' alignItems='center'>
                <Tooltip title='Calendar filter' placement='top'>
                  <IconButton size='small' onClick={event => handleCalendarFilterClick(event)}>
                    <CalendarIcon className={classes.calendarIcon} />
                  </IconButton>
                </Tooltip>

                {canCreatePayment && (
                  <Fragment>
                    <Button disabled={isLoadingData} className={classes.actionButton} onClick={handleAction}>
                      Aksi <ArrowIcon />
                      {isLoadingButton ? (
                        <Fade
                          in={isLoadingButton}
                          style={{
                            transitionDelay: '0ms'
                          }}
                          unmountOnExit
                        >
                          <CircularProgress size={24} className={classes.buttonProgress} />
                        </Fade>
                      ) : (
                        ''
                      )}
                    </Button>
                    <Menu
                      id='list-menu'
                      anchorEl={anchorEl}
                      keepMounted
                      elevation={1}
                      getContentAnchorEl={null}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center'
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center'
                      }}
                    >
                      <MenuItem className={classes.menuList} onClick={handleCreatePayment}>
                        Buat Pembayaran
                      </MenuItem>
                      <MenuItem className={classes.menuList} onClick={handleOpenGiroModal}>
                        Pencairan Giro
                      </MenuItem>
                      <MenuItem className={classes.menuList} onClick={downloadExcel}>
                        {loadingExport ? <CircularProgress color='inherit' size={20} /> : 'Export'}
                      </MenuItem>
                    </Menu>
                    <Button
                      disabled={isLoadingData}
                      className={classes.exportButton}
                      onClick={() => setOpenExportSales(true)}
                      startIcon={<GetApp fontSize='small' />}
                    >
                      Export Pembayaran Penjualan
                    </Button>
                  </Fragment>
                )}
                {isSuperVisor && (
                  <Button onClick={handleCreatePayment} disabled={isLoadingData}>
                    <AddBox fontSize='small' /> &nbsp; Buat Pembayaran
                  </Button>
                )}
                {(isSuperAdmin || isFinance) && !authorizaion && (
                  <Badge
                    color='error'
                    badgeContent={totalAuthorization}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <Description
                      fontSize='large'
                      color='primary'
                      style={{
                        marginLeft: 12,
                        cursor: 'pointer',
                        pointerEvents: 'auto'
                      }}
                      //className={totalAuthorization > 0 ? classes.animationNotif : ''}
                      onClick={() => handleOpenAuthorization()}
                    />
                  </Badge>
                )}
                {(isSuperAdmin || isFinance) && authorizaion && (
                  <IconButton color='primary' aria-label='back' component='span' className={classes.buttonBack}>
                    <ExitToApp fontSize='large' onClick={handleCloseAuthorization} />
                  </IconButton>
                )}
              </Grid>
            </Grid>

            <Collapse in={(isSuperAdmin || isFinance) && authorizaion} unmountOnExit>
              <Grid item container justify='flex-start' alignItems='center' spacing={2}>
                {(isSuperAdmin || isFinance) && authorizaion ? (
                  <Grid item>
                    <Button size='small' value={1} variant={level === 1 && !reject ? 'contained' : 'text'} onClick={handleLevel}>
                      Autorisasi Keuangan
                    </Button>
                  </Grid>
                ) : null}

                {isSuperAdmin && authorizaion ? (
                  <Grid item>
                    <Button size='small' value={2} variant={level === 2 && !reject ? 'contained' : 'text'} onClick={handleLevel}>
                      Autorisasi Penuh
                    </Button>
                  </Grid>
                ) : null}

                {(isSuperAdmin || isFinance) && authorizaion ? (
                  <Grid item>
                    <Button size='small' variant={reject ? 'contained' : 'text'} onClick={handleReject}>
                      Pembayaran Ditolak
                    </Button>
                  </Grid>
                ) : null}
              </Grid>
            </Collapse>

            <Grid item xl={5} lg={5} md={5} sm={6} xs={6}>
              <TextField
                placeholder='Cari berdasarkan nama, no kwitansi'
                value={globalSearch}
                onChange={e => setGlobalSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search />
                    </InputAdornment>
                  )
                }}
                fullWidth
              />
            </Grid>

            <Grid container justify='center' item xl={12} lg={12} md={12} sm={12} xs={12}>
              <PaymentTable
                isLoadingData={isLoadingData}
                invoicePayments={invoicePayments}
                order={order}
                orderBy={orderBy}
                name={name}
                salesName={salesName}
                setSalesName={setSalesName}
                giroNumber={giroNumber}
                setGiroNumber={setGiroNumber}
                code={code}
                invoiceNumber={invoiceNumber}
                totalPay={totalPay}
                status={status}
                payDate={payDate}
                zones={zones}
                selectedZone={selectedZone}
                setSelectedZone={setSelectedZone}
                setInvoiceNumber={setInvoiceNumber}
                setName={setName}
                setCode={setCode}
                setOrder={setOrder}
                setOrderBy={setOrderBy}
                setTotalPay={setTotalPay}
                setStatus={setStatus}
                setPayDate={setPayDate}
                setPaymentMethod={setPaymentMethod}
                paymentMethod={paymentMethod}
                invoicePaymentReturn={invoicePaymentReturn}
                isLoadingCollapse={isLoadingCollapse}
                indexCollapse={indexCollapse}
                openCollapse={openCollapse}
                handleOpenCollapse={handleOpenCollapse}
                handleAction={handleActionAuthorization}
              />
            </Grid>

            <Grid container justify='flex-end' item xl={12} md={12} sm={12}>
              <PaginationCustom
                marginTop='-.4em '
                show={invoicePayments.length > 0}
                sxPagination={{
                  count,
                  boundaryCount: 2,
                  variant: 'outlined',
                  shape: 'rounded',
                  onChange: (event, page) => {
                    setCurrentPage(page);
                  },
                  page: currentPage
                }}
                sxPopover={{
                  anchorOrigin: {
                    vertical: 'top',
                    horizontal: 'right'
                  },
                  transformOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right'
                  }
                }}
                customPageProps={{
                  defaultValue: currentPage,
                  maxValue: count,
                  onSubmit(value) {
                    setCurrentPage(value);
                  }
                }}
              />
            </Grid>
          </Grid>
        </PaperCustom>
      </Container>

      <DateRangeFilter
        openCalendarFilter={openCalendarFilter}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        handleClose={() => {
          setOpenCalendarFilter(false);
        }}
      />

      <Dialog open={openExportSales} onClose={() => setOpenExportSales(false)} maxWidth='xs' fullWidth>
        <DialogTitle>Export Pembayaran Penjualan</DialogTitle>
        <DialogContent>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              fullWidth
              margin='dense'
              id='export-sales-month'
              label='Periode'
              required
              format='MMMM yyyy'
              views={['year', 'month']}
              value={exportSalesMonth}
              onChange={date => {
                if (date) {
                  setExportSalesMonth(date);
                }
              }}
              KeyboardButtonProps={{
                'aria-label': 'periode'
              }}
            />
          </MuiPickersUtilsProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportSales(false)} color='inherit'>
            Batal
          </Button>
          <Button onClick={downloadSalesExcel} color='primary' disabled={loadingExportSales}>
            {loadingExportSales ? <CircularProgress color='inherit' size={20} /> : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentGiroModal
        giroMonthDue={giroMonthDue}
        setOpenSnackbar={setOpenSnackbar}
        openModal={openGiroModal}
        handleCloseModal={handleCloseGiroModal}
        handleSnackBar={handleSnackBar}
        handleUpdatePaymentIndividual={handleUpdatePaymentIndividual}
      />

      <StandardConfirmationDialog
        open={openSnackbar}
        variant={snackbarVariant}
        titleMessage={snackbarVariant === 'success' ? 'Success!' : 'Error!'}
        message={snackbarMessage}
        handleClose={handleCloseSnackbar}
        noCancelButton={true}
      />

      <InvoicePaymentAuthorization
        open={isAction.isOpen}
        handleClose={() => handleActionAuthorization(false, 0)}
        handleSubmit={auhtorization}
        handleReject={rejected}
        isLoading={isAction.isLoading}
        invoicePayment={selectedInvoicePayment}
      />
    </Page>
  );
};

export default PaymentPage;
